import type { ReactNode } from "react"
import { cn } from "@/shared/lib/utils"

const R = 88
const CX = 88
const CY = 88
const ARC = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`
const ARC_LENGTH = Math.PI * R
const TICK_OUTER = R - 8
const TICK_INNER = TICK_OUTER - 4
const TICKS = Array.from({ length: 13 }, (_, i) => (i * Math.PI) / 12)

interface EiSpeedometerProps {
	value: number
	ratio: number
	valueClassName: string
	children?: ReactNode
	className?: string
	contentClassName: string
}

function EiSpeedometer({
	value,
	ratio,
	valueClassName,
	children,
	className,
	contentClassName,
}: EiSpeedometerProps) {
	const filled = Math.min(1, Math.max(0, ratio))

	return (
		<div className={cn("relative mx-auto w-full shrink-0", className)}>
			<svg
				viewBox="0 0 176 90"
				fill="none"
				className="absolute inset-x-0 top-0 block w-full overflow-visible"
				aria-hidden="true"
			>
				<path
					d={ARC}
					stroke="#FFFFFF"
					strokeWidth="2"
					style={{ mixBlendMode: "soft-light" }}
				/>
				{TICKS.map((angle, index) => {
					const active = index / (TICKS.length - 1) <= filled
					return (
						<line
							key={angle}
							x1={CX - Math.cos(angle) * TICK_OUTER}
							y1={CY - Math.sin(angle) * TICK_OUTER}
							x2={CX - Math.cos(angle) * TICK_INNER}
							y2={CY - Math.sin(angle) * TICK_INNER}
							stroke={active ? "#5DF7FF" : "#FFFFFF"}
							strokeWidth="1"
							style={active ? undefined : { mixBlendMode: "soft-light" }}
						/>
					)
				})}
				<path
					d={ARC}
					stroke="#5DF7FF"
					strokeWidth="4"
					strokeDasharray={ARC_LENGTH}
					strokeDashoffset={ARC_LENGTH * (1 - filled)}
				/>
			</svg>

			<div
				className={cn(
					"absolute left-1/2 flex -translate-x-1/2 flex-col items-center text-center whitespace-nowrap text-[#5DF7FF]",
					contentClassName,
				)}
			>
				<span
					className={cn("font-display leading-none font-light", valueClassName)}
				>
					{value}
				</span>
				{children}
			</div>
		</div>
	)
}

export { EiSpeedometer }
